import { type HTMLAttributes, forwardRef, useMemo } from 'react';

export interface HeatmapData {
  date: string;
  completed: boolean;
}

export interface HeatmapProps extends HTMLAttributes<HTMLDivElement> {
  /** Array of date/completion records */
  data: HeatmapData[];
  /** Start date (inclusive) in YYYY-MM-DD format */
  startDate: string;
  /** End date (inclusive) in YYYY-MM-DD format */
  endDate: string;
  /** Color for completed cells (CSS color value) */
  color?: string;
}

function parseDate(dateStr: string): Date {
  const parts = dateStr.split('-').map(Number);
  const year = parts[0] ?? 0;
  const month = parts[1] ?? 1;
  const day = parts[2] ?? 1;
  return new Date(year, month - 1, day);
}

function formatDateStr(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getDaysBetween(start: string, end: string): string[] {
  const dates: string[] = [];
  const current = parseDate(start);
  const endDate = parseDate(end);

  while (current <= endDate) {
    dates.push(formatDateStr(current));
    current.setDate(current.getDate() + 1);
  }

  return dates;
}

function getDayOfWeek(dateStr: string): number {
  const date = parseDate(dateStr);
  // Convert to Mon=0, Tue=1, ..., Sun=6
  const day = date.getDay();
  return day === 0 ? 6 : day - 1;
}

const CELL_SIZE = 12;

export const Heatmap = forwardRef<HTMLDivElement, HeatmapProps>(
  ({ data, startDate, endDate, color = 'var(--color-primary)', className = '', ...props }, ref) => {
    const completionMap = useMemo(() => {
      const map = new Map<string, boolean>();
      for (const entry of data) {
        map.set(entry.date, entry.completed);
      }
      return map;
    }, [data]);

    const grid = useMemo(() => {
      const allDates = getDaysBetween(startDate, endDate);
      // Organize into columns (weeks), each column has 7 rows (Mon-Sun)
      const columns: (string | null)[][] = [];

      if (allDates.length === 0) return columns;

      // Start first column with padding for days before startDate's weekday
      const firstDate = allDates[0];
      if (!firstDate) return columns;
      const firstDayOfWeek = getDayOfWeek(firstDate);
      let currentColumn: (string | null)[] = Array(firstDayOfWeek).fill(null);

      for (const date of allDates) {
        if (currentColumn.length === 7) {
          columns.push(currentColumn);
          currentColumn = [];
        }
        currentColumn.push(date);
      }

      // Pad last column to 7 rows
      while (currentColumn.length < 7) {
        currentColumn.push(null);
      }
      columns.push(currentColumn);

      return columns;
    }, [startDate, endDate]);

    return (
      <div
        ref={ref}
        className={['inline-flex gap-[2px]', className].join(' ')}
        role="grid"
        aria-label="Activity heatmap"
        {...props}
      >
        {grid.map((column, colIndex) => (
          <div key={colIndex} className="flex flex-col gap-[2px]" role="row">
            {column.map((date, rowIndex) => {
              if (date === null) {
                return (
                  <div
                    key={`empty-${colIndex}-${rowIndex}`}
                    style={{ width: CELL_SIZE, height: CELL_SIZE }}
                  />
                );
              }

              const completed = completionMap.get(date) ?? false;

              return (
                <div
                  key={date}
                  role="gridcell"
                  aria-label={
                    completed
                      ? `Completed on ${date}`
                      : `Not completed on ${date}`
                  }
                  title={`${date}${completed ? ' - Completed' : ' - Not completed'}`}
                  className="rounded-[2px]"
                  style={{
                    width: CELL_SIZE,
                    height: CELL_SIZE,
                    backgroundColor: completed ? color : 'var(--color-bg-secondary)',
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>
    );
  }
);

Heatmap.displayName = 'Heatmap';
